import { LoadingButton } from '@mui/lab';
import { Alert, Button, Collapse, Divider, Grid, TextField } from '@mui/material';
import { FormikValues } from 'formik';
import { isString } from 'lodash';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import ReactInputMask from 'react-input-mask';
import { useLanguageTabs } from 'src/hooks/useLanguageTabs';
import { useEdit } from '../../../hooks';
import {
  clearOrganization,
  clearOrganizationRequest,
  createOrganization,
  editOrganization,
  forceGetOrganizationWarehouse,
  getOrganization,
} from '../../../thunks/organization-thunks';
import {
  FIELD_ADDRESS,
  FIELD_ADDRESS_OZ,
  FIELD_ADDRESS_RU,
  FIELD_ADDRESS_UZ,
  FIELD_EMAIL,
  FIELD_PHONE,
  FIELD_STIR,
  FIELD_TITLE,
  FIELD_TITLE_OZ,
  FIELD_TITLE_RU,
  FIELD_TITLE_UZ,
  OrganizationsEditValidationForm,
} from './organizations-edit-schema';

export interface OrganizationsEditFormProps {
  id?: string | number;
  onCancel?: () => void;
  onSubmit: (values: FormikValues) => void;
}

export const OrganizationsEditForm: ComponentType<OrganizationsEditFormProps> = ({
  id,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { tabLang, renderTabsButtons } = useLanguageTabs();
  const { isLoading, isFetching, error, formik, handleFieldChange, hasError, getErrorMessage } =
    useEdit({
      id,
      entity: 'organization',
      initialValues: {
        [FIELD_TITLE]: {},
        [FIELD_ADDRESS]: {},
        [FIELD_PHONE]: '',
        [FIELD_EMAIL]: '',
        [FIELD_STIR]: '',
      },
      validationSchema: OrganizationsEditValidationForm,
      onGetWareHouse: forceGetOrganizationWarehouse,
      onGetOne: getOrganization,
      onClearOne: clearOrganization,
      onClear: clearOrganizationRequest,
      onCreate: createOrganization,
      onEdit: editOrganization,
      onSubmit: onSubmit,
    });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container mb={3} direction="column">
        <Grid item xs={12} mb={2}>
          {renderTabsButtons()}
        </Grid>

        {tabLang === 'ru' && (
          <>
            <Grid item xs={12}>
              <TextField
                {...formik.getFieldProps(FIELD_TITLE_RU)}
                onChange={(e: any) => handleFieldChange(FIELD_TITLE_RU, e)}
                error={hasError(FIELD_TITLE_RU)}
                helperText={getErrorMessage(FIELD_TITLE_RU)}
                label={t('organizations.name', 'Название организации') + ' (' + t('russian') + ')'}
                placeholder={t('organizations.name.placeholder', 'Введите название организации')}
                disabled={isLoading || isFetching}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...formik.getFieldProps(FIELD_ADDRESS_RU)}
                onChange={(e: any) => handleFieldChange(FIELD_ADDRESS_RU, e)}
                error={hasError(FIELD_ADDRESS_RU)}
                helperText={getErrorMessage(FIELD_ADDRESS_RU)}
                label={t('organizations.address', 'Адрес организации') + ' (' + t('russian') + ')'}
                placeholder={t('organizations.address.placeholder', 'Введите адрес организации')}
                disabled={isLoading || isFetching}
                fullWidth
              />
            </Grid>
          </>
        )}
        {tabLang === 'uz' && (
          <>
            <Grid item xs={12}>
              <TextField
                {...formik.getFieldProps(FIELD_TITLE_UZ)}
                onChange={(e: any) => handleFieldChange(FIELD_TITLE_UZ, e)}
                error={hasError(FIELD_TITLE_UZ)}
                helperText={getErrorMessage(FIELD_TITLE_UZ)}
                label={t('organizations.name', 'Название организации') + ' (' + t('uzbek') + ')'}
                placeholder={t('organizations.name.placeholder', 'Введите название организации')}
                disabled={isLoading || isFetching}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...formik.getFieldProps(FIELD_ADDRESS_UZ)}
                onChange={(e: any) => handleFieldChange(FIELD_ADDRESS_UZ, e)}
                error={hasError(FIELD_ADDRESS_UZ)}
                helperText={getErrorMessage(FIELD_ADDRESS_UZ)}
                label={t('organizations.address', 'Адрес организации') + ' (' + t('uzbek') + ')'}
                placeholder={t('organizations.address.placeholder', 'Введите адрес организации')}
                disabled={isLoading || isFetching}
                fullWidth
              />
            </Grid>
          </>
        )}
        {tabLang === 'oz' && (
          <>
            <Grid item xs={12}>
              <TextField
                {...formik.getFieldProps(FIELD_TITLE_OZ)}
                onChange={(e: any) => handleFieldChange(FIELD_TITLE_OZ, e)}
                error={hasError(FIELD_TITLE_OZ)}
                helperText={getErrorMessage(FIELD_TITLE_OZ)}
                label={t('organizations.name', 'Название организации') + ' (' + t('ozbek') + ')'}
                placeholder={t('organizations.name.placeholder', 'Введите название организации')}
                disabled={isLoading || isFetching}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...formik.getFieldProps(FIELD_ADDRESS_OZ)}
                onChange={(e: any) => handleFieldChange(FIELD_ADDRESS_OZ, e)}
                error={hasError(FIELD_ADDRESS_OZ)}
                helperText={getErrorMessage(FIELD_ADDRESS_OZ)}
                label={t('organizations.address', 'Адрес организации') + ' (' + t('ozbek') + ')'}
                placeholder={t('organizations.address.placeholder', 'Введите адрес организации')}
                disabled={isLoading || isFetching}
                fullWidth
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <ReactInputMask
            mask="+\9\9\8 99 999 99 99"
            {...formik.getFieldProps(FIELD_PHONE)}
            onChange={(e: any) => handleFieldChange(FIELD_PHONE, e)}
            disabled={isLoading || isFetching}
            maskChar=""
          >
            {() => (
              <TextField
                fullWidth
                helperText={getErrorMessage(FIELD_PHONE)}
                error={hasError(FIELD_PHONE)}
                label={t('profile.phone', 'Номер телефона')}
                placeholder={t('profile.phone.placeholder', 'Введите номер телефона')}
              />
            )}
          </ReactInputMask>
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...formik.getFieldProps(FIELD_EMAIL)}
            onChange={(e: any) => handleFieldChange(FIELD_EMAIL, e)}
            error={hasError(FIELD_EMAIL)}
            helperText={getErrorMessage(FIELD_EMAIL)}
            label={t('profile.email', 'Email')}
            placeholder={t('profile.email.placeholder', 'Введите email')}
            disabled={isLoading || isFetching}
            fullWidth
            type="email"
          />
        </Grid>
        <Grid item xs={12}>
          <ReactInputMask
            mask="999999999"
            {...formik.getFieldProps(FIELD_STIR)}
            onChange={(e: any) => handleFieldChange(FIELD_STIR, e)}
            disabled={isLoading || isFetching}
            maskChar=""
          >
            {() => (
              <TextField
                error={hasError(FIELD_STIR)}
                helperText={getErrorMessage(FIELD_STIR)}
                label={t('organizations.stir', 'ИНН')}
                placeholder={t('organizations.stir.placeholder', 'Введите ИНН')}
                fullWidth
              />
            )}
          </ReactInputMask>
        </Grid>
      </Grid>
      {isString(error) && (
        <Collapse in={!!error}>
          <Alert severity="error">{error}</Alert>
        </Collapse>
      )}
      <Divider sx={{ mt: 3, mb: 4 }} />
      <Grid container columnSpacing={2} justifyContent="space-between">
        <Grid item>
          {onCancel && (
            <Button onClick={onCancel} variant="outlined" size="large" disabled={isLoading}>
              {t('cancel', 'Отмена')}
            </Button>
          )}
        </Grid>
        <Grid item>
          <LoadingButton
            loading={isLoading || isFetching}
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
          >
            {t('action.save', 'Сохранить')}
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  );
};
